/**
 * Device-local push setup. Never prompts on page load.
 * Inputs: browser capability. Outputs: setup status and subscription helpers.
 */

import type { DevicePreferences, PushDevice, SetupStatus } from "@shared/push.ts";
import { defaultPreferences } from "@shared/push.ts";

const DEVICE_KEY = "tt-device-id";
const DISMISS_KEY = "tt-alerts-setup-dismissed";

export function readDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    return "anonymous-device";
  }
}

export function isStandalone(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches
    || ("standalone" in navigator && Boolean((navigator as { standalone?: boolean }).standalone));
}

export function isIos(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function detectSetupStatus(): SetupStatus {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return "unsupported";
  }
  if (isIos() && !isStandalone()) return "needs_install";
  if (Notification.permission === "denied") return "blocked";
  if (Notification.permission === "default") return "permission_not_requested";
  return "subscription_active";
}

/** Stored setup can lag behind the OS permission. Capability wins. */
export function effectiveSetupStatus(stored: SetupStatus | null | undefined, detected: SetupStatus): SetupStatus {
  if (detected === "unsupported" || detected === "needs_install" || detected === "blocked" || detected === "permission_not_requested") {
    return detected;
  }
  if (stored === "test_confirmed" || stored === "test_sent" || stored === "subscription_active") return stored;
  return detected;
}

export function setupDismissedLocally(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissSetupLocally() {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* quota */
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replaceAll("-", "+").replaceAll("_", "/"));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export async function enablePushSubscription(vapidPublicKey: string): Promise<PushSubscription> {
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
  });
}

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) return null;
  const ready = navigator.serviceWorker.ready.then((reg) => reg, () => null);
  const timeout = new Promise<null>((resolve) => {
    setTimeout(() => resolve(null), 1500);
  });
  const reg = await Promise.race([ready, timeout]);
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function dropPushSubscription(): Promise<void> {
  const sub = await currentPushSubscription();
  await sub?.unsubscribe();
}

export type PushConfig = {
  vapidPublicKey: string | null;
  pushEnabled: boolean;
  vapidConfigured: boolean;
  notice: string;
  profiles: Array<{ id: string; label: string; householdId: string }>;
};

async function parse(res: Response) {
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new Error(String(data.error ?? `Request failed (${res.status})`));
  return data;
}

export async function fetchPushConfig(): Promise<PushConfig> {
  return parse(await fetch("/api/push/config", { credentials: "same-origin" })) as Promise<PushConfig> as unknown as PushConfig;
}

export async function fetchThisDevice(): Promise<PushDevice | null> {
  const res = await fetch(`/api/push/device?deviceId=${encodeURIComponent(readDeviceId())}`, { credentials: "same-origin" });
  const data = await parse(res);
  return (data.device as PushDevice | null) ?? null;
}

export async function saveDeviceRegistration(input: {
  subscription?: PushSubscriptionJSON | null;
  profileId?: string | null;
  householdId?: string | null;
  preferences?: DevicePreferences;
  dismissedSetup?: boolean;
  installedPwa?: boolean;
}) {
  return parse(
    await fetch("/api/push/subscribe", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        deviceId: readDeviceId(),
        subscription: input.subscription ?? undefined,
        profileId: input.profileId,
        householdId: input.householdId,
        preferences: input.preferences ?? defaultPreferences(),
        dismissedSetup: input.dismissedSetup,
        userAgent: navigator.userAgent,
        installedPwa: input.installedPwa ?? isStandalone(),
      }),
    }),
  );
}

export async function sendTestPush() {
  return parse(
    await fetch("/api/push/test", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId: readDeviceId() }),
    }),
  );
}

export async function confirmTestPush() {
  return parse(
    await fetch("/api/push/test/confirm", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId: readDeviceId() }),
    }),
  );
}

export async function revokeThisDevice() {
  try {
    await dropPushSubscription();
  } catch {
    /* still revoke server-side */
  }
  await fetch("/api/push/unsubscribe", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deviceId: readDeviceId() }),
    signal: AbortSignal.timeout(4000),
  }).catch(() => undefined);
}

export async function fetchUpdates(): Promise<{ alerts: import("@shared/push.ts").InboxAlert[]; unread: number }> {
  return (await parse(
    await fetch(`/api/updates?deviceId=${encodeURIComponent(readDeviceId())}`, { credentials: "same-origin" }),
  )) as { alerts: import("@shared/push.ts").InboxAlert[]; unread: number };
}

export async function markUpdateSeen(id: string) {
  return parse(
    await fetch(`/api/updates/${encodeURIComponent(id)}/seen`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId: readDeviceId() }),
    }),
  );
}

export async function fetchTdac() {
  return parse(await fetch("/api/tasks/tdac", { credentials: "same-origin" }));
}

export async function saveTdac(body: { travellerKey?: string; complete?: boolean; confirmationAttached?: boolean }) {
  return parse(
    await fetch("/api/tasks/tdac", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  );
}

export async function reportPackRevision(revision: number) {
  await fetch("/api/push/pack-status", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deviceId: readDeviceId(), revision }),
  }).catch(() => undefined);
}

export async function fetchPushStatus() {
  return parse(await fetch("/api/push/status", { credentials: "same-origin" }));
}

export function statusCopy(status: SetupStatus): { title: string; detail: string } {
  switch (status) {
    case "unsupported":
      return { title: "This browser cannot receive trip alerts", detail: "Use the Updates inbox and the airline’s own notifications as backup." };
    case "needs_install":
      return { title: "Add to Home Screen first", detail: "On iPhone, Safari only offers notifications after you add this site to the Home Screen and open it from that icon." };
    case "permission_not_requested":
      return { title: "Notifications not enabled yet", detail: "This phone will not prompt until you tap Enable notifications." };
    case "blocked":
      return { title: "Notifications are blocked", detail: "Open the browser or system settings for this site, allow notifications, then return here. On iPhone that is Settings → Notifications after installing to the Home Screen." };
    case "subscription_active":
      return { title: "This phone is registered", detail: "A test confirmation is still needed before we treat alerts as proven on this device." };
    case "test_sent":
      return { title: "Test sent", detail: "A provider accepting the message is not proof it appeared. Confirm if you saw it." };
    case "test_confirmed":
      return { title: "Test confirmed on this phone", detail: "That is not a permanent delivery guarantee. Phones can go offline or silence alerts." };
  }
}
